import { useEffect, useState } from "react";
import { Link } from "../components/LocaleLink";
import { motion } from "framer-motion";
import {
  Home as HomeIcon, KeyRound, Hammer, TrendingUp, ShieldCheck, Sparkles,
  ArrowRight, Quote, Users, Check, MapPin, Calendar as CalendarIcon, TrendingUp as TrendingUpIcon, Star,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { fetchProperties, optimizeImg } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { PropertyCard } from "../components/PropertyCard";
import { PressStrip } from "../components/PressStrip";
import { OwnerEstimateBanner } from "../components/OwnerEstimateBanner";
import { track } from "../lib/analytics";
import { CASE_STUDIES } from "../data/caseStudies";

const HERO = "https://images.unsplash.com/photo-1763114298920-eaac2534e739";
const IMG_ACQ = "https://images.unsplash.com/photo-1763506754901-fb5be9c1add7";
const IMG_CONCIERGE = "https://images.unsplash.com/photo-1599383885524-8ab14dee8249";

const SERVICE_ICONS = [HomeIcon, KeyRound, Hammer, TrendingUp, ShieldCheck, Sparkles];

const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProperties({ featured: true }).then((d) => setFeatured(d.slice(0, 3))).catch(() => {});
  }, []);

  const stats = t("stats");
  const services = t("services.items");
  const values = t("values.items");
  const testimonials = t("testimonials.items");
  const team = t("team.members");
  const tracks = t("howItWorks.tracks");
  const [activeTrack, setActiveTrack] = useState(0);
  const howToDest = { buy: "/buy", rental: "/owner", management: "/owner" };

  return (
    <div data-testid="home-page">
      <Seo title={t("meta.homeTitle")} description={t("hero.subtitle")} />
      {/* HERO */}
      <section className="relative w-full h-[92vh] min-h-[620px] flex items-end vl-grain overflow-hidden">
        <img src={optimizeImg(HERO, 1920)} alt="Luxury French Riviera villa" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] w-full pb-20 md:pb-32">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className={overline}>
            {t("hero.overline")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="mt-6 font-headings font-normal text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight text-foreground max-w-4xl whitespace-pre-line"
          >
            {t("hero.title")}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="mt-8 font-body font-light text-base sm:text-lg text-foreground/80 max-w-2xl leading-relaxed">
            {t("hero.subtitle")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }} className="mt-10">
            <div className="flex flex-wrap gap-4">
              <Link to="/owner" data-testid="hero-cta-owner" onClick={() => track("property_management_cta_click", { cta_location: "home_hero", lead_type: "owner" })} className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
                {t("hero.cta1")} <ArrowRight size={15} />
              </Link>
              <Link to="/buy" data-testid="hero-cta-buy" onClick={() => track("buyer_cta_click", { cta_location: "home_hero", lead_type: "buyer" })} className="inline-flex items-center gap-2 border border-foreground/30 text-foreground hover:border-primary hover:text-primary px-8 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
                {t("hero.cta2")}
              </Link>
            </div>
            <p data-testid="hero-cta-support" className="mt-4 font-body font-light text-sm text-foreground/65 max-w-xl">{t("hero.ctaSupport")}</p>
            <Link to="/properties" data-testid="hero-cta-properties" className="mt-3 inline-flex items-center gap-1.5 font-accent text-[11px] uppercase tracking-[0.25em] text-foreground/60 hover:text-primary transition-colors">
              {t("hero.cta3")} <ArrowRight size={12} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-secondary border-y border-border/60">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center lg:text-left">
              <p className="font-headings text-5xl md:text-6xl text-primary">{s.value}</p>
              <p className="mt-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{s.label}</p>
            </Reveal>
          ))}
        </div>
        {/* INTENT SELECTOR — secondary navigation aid below the stats bar */}
        <div data-testid="intent-selector" className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] pb-14">
          <Reveal>
            <p className="font-accent text-[11px] uppercase tracking-[0.25em] text-primary mb-4">
              {t("intents.prompt")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: "buy", icon: KeyRound, to: "/buy", label: t("intents.buy") },
                { key: "own", icon: HomeIcon, to: "/owner", label: t("intents.own") },
                { key: "compare", icon: TrendingUp, to: "/invest", label: t("intents.compare") },
              ].map((it) => (
                <Link
                  key={it.key}
                  to={it.to}
                  data-testid={`intent-${it.key}`}
                  className="group flex items-center justify-between gap-3 border border-border/60 bg-background/40 hover:border-primary px-5 py-4 transition-colors duration-300"
                >
                  <span className="flex items-center gap-3">
                    <it.icon size={17} strokeWidth={1.6} className="text-primary shrink-0" />
                    <span className="font-body text-sm text-foreground/85 group-hover:text-foreground transition-colors">{it.label}</span>
                  </span>
                  <ArrowRight size={14} className="text-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="scroll-mt-24 py-24 md:py-32 lg:py-40">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 relative">
            <Reveal>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={optimizeImg(IMG_ACQ, 900)} alt="Nice architecture" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <Reveal>
              <p className={overline}>{t("about.overline")}</p>
              <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground max-w-xl">{t("about.title")}</h2>
            </Reveal>
            <div className="mt-8 space-y-6 font-body font-light text-base sm:text-lg text-foreground/75 leading-relaxed max-w-2xl">
              <Reveal delay={0.1}><p>{t("about.p1")}</p></Reveal>
              <Reveal delay={0.2}><p>{t("about.p2")}</p></Reveal>
              <Reveal delay={0.3}><p>{t("about.p3")}</p></Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="scroll-mt-24 bg-secondary py-24 md:py-32 lg:py-40 border-y border-border/60">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <div className="max-w-2xl">
            <Reveal>
              <p className={overline}>{t("services.overline")}</p>
              <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{t("services.title")}</h2>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/50">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={i} delay={(i % 3) * 0.1} className="bg-secondary">
                  <div className="group p-10 h-full hover:bg-card transition-colors duration-500">
                    <Icon size={26} className="text-primary" strokeWidth={1.4} />
                    <h3 className="mt-6 font-headings text-2xl text-foreground">{s.title}</h3>
                    <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="py-24 md:py-32 lg:py-40">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <Reveal className="max-w-2xl">
              <p className={overline}>{t("featured.overline")}</p>
              <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{t("featured.title")}</h2>
              <p className="mt-5 font-body font-light text-foreground/70 leading-relaxed">{t("featured.subtitle")}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/properties" data-testid="featured-view-all" className="inline-flex items-center gap-2 border border-foreground/30 text-foreground hover:border-primary hover:text-primary px-8 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300 whitespace-nowrap">
                {t("featured.viewAll")} <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}><PropertyCard property={p} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-secondary py-24 md:py-32 border-y border-border/60">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal className="max-w-2xl mb-16">
            <p className={overline}>{t("values.overline")}</p>
            <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{t("values.title")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="border-t border-primary/40 pt-6">
                  <span className="font-headings text-primary text-4xl">0{i + 1}</span>
                  <h3 className="mt-4 font-headings text-2xl text-foreground">{v.title}</h3>
                  <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section id="team" data-testid="team-section" className="scroll-mt-24 py-24 md:py-32 lg:py-40">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal className="max-w-2xl mb-16">
            <p className={overline}>{t("team.overline")}</p>
            <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{t("team.title")}</h2>
            <p className="mt-6 font-body font-light text-foreground/70 leading-relaxed">{t("team.subtitle")}</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
            {team.map((m, i) => {
              const initial = (m.name || "").trim().charAt(0);
              return (
                <Reveal key={m.name} delay={i * 0.08}>
                  <div
                    data-testid={`team-card-${m.name.toLowerCase()}`}
                    className="border border-border/60 bg-secondary p-6 lg:p-7 flex flex-col items-start gap-4 h-full hover:border-primary/50 transition-colors"
                  >
                    {m.photo ? (
                      <div className="relative w-full aspect-square overflow-hidden border border-primary/20 bg-background">
                        <img
                          src={m.photo}
                          alt={`${m.name} — ${m.role}`}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                        />
                        <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-primary/10" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-full border border-primary/40 bg-primary/5 flex items-center justify-center">
                        <span className="font-headings text-2xl text-primary" aria-hidden="true">{initial}</span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-headings text-lg text-foreground leading-tight">{m.name}</h3>
                      <p className="mt-1.5 font-accent text-[10px] uppercase tracking-[0.22em] text-primary/85 leading-snug">{m.role}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.4}>
            <div className="mt-14 border-t border-border/50 pt-6 flex items-center gap-3 text-foreground/60">
              <Users size={16} className="text-primary" />
              <p className="font-body font-light text-sm">One accountable person per dimension. No handovers, no dilution of responsibility.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 lg:py-40 vl-grain relative bg-secondary/40 border-y border-border/60">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal className="max-w-2xl mb-16">
            <p className={overline}>{t("testimonials.overline")}</p>
            <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{t("testimonials.title")}</h2>
            <div data-testid="aggregate-rating-badge" className="mt-6 inline-flex items-center gap-3 border border-primary/40 bg-card px-5 py-3">
              <span className="flex items-center gap-0.5" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={15} className="text-primary fill-current" />
                ))}
              </span>
              <span className="font-headings text-xl text-foreground whitespace-nowrap">4.9<span className="text-foreground/50 text-base"> / 5</span></span>
              <span className="font-accent text-[10px] uppercase tracking-[0.2em] text-foreground/60">{t("testimonials.ratingLine")}</span>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((tm, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="bg-card border border-border/50 p-8 lg:p-10 h-full flex flex-col hover:border-primary/40 transition-colors"
                >
                  <Quote size={26} className="text-primary shrink-0" />
                  <blockquote className="mt-6 font-headings text-lg lg:text-xl leading-relaxed text-foreground/90 italic flex-1">&ldquo;{tm.quote}&rdquo;</blockquote>
                  <figcaption className="mt-8 pt-5 border-t border-border/40">
                    <span className="flex items-center gap-0.5 mb-2" aria-label="5 out of 5 stars">
                      {[0, 1, 2, 3, 4].map((s) => (
                        <Star key={s} size={12} className="text-primary fill-current" />
                      ))}
                    </span>
                    <p className="font-headings text-base text-foreground leading-tight">{tm.author}</p>
                    {tm.location ? (
                      <p className="mt-1 font-accent text-[10px] uppercase tracking-[0.28em] text-primary/85">{tm.location}</p>
                    ) : null}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" data-testid="howitworks-section" className="scroll-mt-24 py-24 md:py-32 lg:py-40">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal className="max-w-2xl mb-14">
            <p className={overline}>{t("howItWorks.overline")}</p>
            <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{t("howItWorks.title")}</h2>
            <p className="mt-6 font-body font-light text-foreground/70 leading-relaxed">{t("howItWorks.subtitle")}</p>
          </Reveal>

          {/* Track selector — native buttons, no Radix */}
          <div className="flex flex-wrap gap-2 mb-10" role="tablist" data-testid="howitworks-tracks">
            {tracks.map((tr, i) => (
              <button
                key={tr.key}
                type="button"
                role="tab"
                aria-selected={activeTrack === i}
                onClick={() => setActiveTrack(i)}
                data-testid={`howitworks-track-${tr.key}`}
                className={`px-5 py-3 font-accent text-[11px] uppercase tracking-[0.22em] border transition-all ${
                  activeTrack === i
                    ? "bg-primary/15 border-primary text-primary"
                    : "border-border/60 text-foreground/60 hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {tr.title}
              </button>
            ))}
          </div>

          <Reveal key={tracks[activeTrack].key}>
            <div data-testid={`howitworks-panel-${tracks[activeTrack].key}`} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
              {tracks[activeTrack].steps.map((step, i) => (
                <div
                  key={i}
                  className="border border-border/60 bg-secondary p-6 lg:p-7 flex flex-col gap-3 relative overflow-hidden hover:border-primary/50 transition-colors"
                >
                  <p className="font-headings text-4xl text-primary/25 leading-none absolute top-3 right-4">{String(i + 1).padStart(2, "0")}</p>
                  <div className="w-8 h-8 rounded-full border border-primary/50 bg-primary/5 flex items-center justify-center">
                    <Check size={13} className="text-primary" />
                  </div>
                  <h3 className="font-headings text-lg text-foreground leading-tight">{step.title}</h3>
                  <p className="font-body font-light text-sm text-foreground/70 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <Link
                to={howToDest[tracks[activeTrack].key] || "/contact"}
                onClick={() => howToDest[tracks[activeTrack].key] && track(tracks[activeTrack].key === "buy" ? "buyer_cta_click" : "property_management_cta_click", { cta_location: "home_how_it_works", lead_type: tracks[activeTrack].key === "buy" ? "buyer" : "owner" })}
                data-testid="howitworks-cta"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3.5 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300"
              >
                {t("cta.button")} <ArrowRight size={14} />
              </Link>
              <p className="font-accent text-[11px] uppercase tracking-[0.28em] text-foreground/50">
                Nice · Villefranche · Mont Boron · Monaco
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section id="case-studies" data-testid="case-studies-section" className="scroll-mt-24 py-24 md:py-32 lg:py-40 vl-grain relative bg-secondary/40 border-y border-border/60">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal className="max-w-2xl mb-16">
            <p className={overline}>{t("caseStudies.overline")}</p>
            <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">
              {t("caseStudies.title")}
            </h2>
            <p className="mt-6 font-body font-light text-foreground/70 leading-relaxed">{t("caseStudies.subtitle")}</p>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {CASE_STUDIES.map((cs, i) => (
              <Reveal key={cs.id} delay={(i % 3) * 0.1}>
                <article
                  data-testid={`case-study-${cs.id}`}
                  className="bg-card border border-border/50 p-8 lg:p-10 flex flex-col h-full hover:border-primary/40 transition-colors"
                >
                  {/* Header */}
                  <div className="pb-6 border-b border-border/40">
                    <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-primary/85 flex items-center gap-2">
                      <MapPin size={11} /> {cs.location}
                    </p>
                    <h3 className="mt-3 font-headings text-2xl leading-tight text-foreground">{cs.name}</h3>
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-body text-xs text-foreground/60">
                      <span className="inline-flex items-center gap-1.5"><CalendarIcon size={11} className="text-primary/70" /> {t("caseStudies.managedSince")} {cs.since}</span>
                      <span className="inline-flex items-center gap-1.5">{cs.ownerFlag} {t("caseStudies.owner")}: {cs.ownerCountry}</span>
                    </div>
                  </div>

                  {/* Challenge */}
                  <div className="pt-6">
                    <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-primary/85">{t("caseStudies.challenge")}</p>
                    <p className="mt-2 font-body font-light text-sm text-foreground/80 leading-relaxed">{cs.challenge}</p>
                  </div>

                  {/* Actions */}
                  <div className="pt-6">
                    <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-primary/85">{t("caseStudies.actions")}</p>
                    <ul className="mt-3 space-y-2">
                      {cs.actions.map((a, idx) => (
                        <li key={idx} className="flex items-start gap-2 font-body font-light text-sm text-foreground/80 leading-relaxed">
                          <Check size={13} className="text-primary shrink-0 mt-1" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Results / Metrics */}
                  <div className="pt-6 mt-auto">
                    <p className="font-accent text-[10px] uppercase tracking-[0.28em] text-primary/85 flex items-center gap-2">
                      <TrendingUpIcon size={11} /> {t("caseStudies.results")}
                    </p>
                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                      {cs.metrics.map((m, idx) => (
                        <div key={idx} className="border-l-2 border-primary/40 pl-3">
                          <dt className="font-accent text-[9px] uppercase tracking-[0.22em] text-foreground/50">{m.label}</dt>
                          <dd className="mt-0.5 font-headings text-lg text-foreground leading-tight">
                            {m.change ? (
                              <span className="text-primary">{m.change}</span>
                            ) : m.from && m.to ? (
                              <span>
                                <span className="text-foreground/50 text-sm">{m.from}</span>
                                <span className="mx-1.5 text-foreground/40">→</span>
                                <span className="text-primary">{m.to}</span>
                              </span>
                            ) : (
                              <span className="text-primary">{m.to}</span>
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>

                  {/* Pull quote */}
                  {cs.quote ? (
                    <blockquote className="mt-8 pt-6 border-t border-border/40 font-headings text-base italic text-foreground/85 leading-relaxed">
                      <Quote size={18} className="text-primary mb-3" />
                      &ldquo;{cs.quote}&rdquo;
                    </blockquote>
                  ) : null}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <OwnerEstimateBanner location="home_after_case_studies" className="pb-24 md:pb-32" />

      <PressStrip />

      {/* CTA */}
      <section className="relative overflow-hidden vl-grain">
        <img src={optimizeImg(IMG_CONCIERGE, 1600)} alt="Mediterranean lifestyle" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] py-28 md:py-40 text-center">
          <Reveal>
            <h2 className="font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight text-foreground max-w-3xl mx-auto">{t("cta.title")}</h2>
            <p className="mt-6 font-body font-light text-lg text-foreground/75 max-w-xl mx-auto">{t("cta.subtitle")}</p>
            <Link to="/contact" data-testid="home-cta-button" className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
              {t("cta.button")} <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
